import React, { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import logoPresida from '../../images/logo-presida-vertical.png';
import bgImage from '../../images/presida_BG.jpg';
import { supabase } from '../../lib/supabase';

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const { theme, toggleTheme } = useTheme();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Por favor, informe seu email');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Por favor, informe um email válido');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/redefinir-senha`,
      });

      if (resetError) {
        console.error('Error requesting password reset:', resetError);
        if (resetError.message.includes('rate limit')) {
          setError('Muitas tentativas. Por favor, aguarde alguns minutos e tente novamente.');
        } else {
          setError('Erro ao enviar email de recuperação. Tente novamente.');
        }
      } else {
        setSuccess(true);
        setEmail('');
      }
    } catch (err) {
      console.error('Unexpected error during password reset:', err);
      setError('Erro inesperado ao enviar email de recuperação');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-black/50" />

      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors z-10"
      >
        {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>

      <div className="card p-8 w-full max-w-md relative z-10 bg-white/95 dark:bg-dark-paper/95 backdrop-blur-sm">
        <div className="flex flex-col items-center mb-6">
          <img src={logoPresida} alt="Presida.Club" className="h-24 w-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Recuperar Senha</h2>
          <p className="text-gray-500 dark:text-gray-400 mt-2 text-center">
            {success
              ? 'Email enviado com sucesso!'
              : 'Informe seu email para receber instruções de recuperação'}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {success ? (
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 p-4 rounded-lg text-sm">
              <p className="font-medium mb-2">Email enviado!</p>
              <p>
                Verifique sua caixa de entrada e siga as instruções para redefinir sua senha.
                Se não receber o email em alguns minutos, verifique sua pasta de spam.
              </p>
            </div>

            <Link
              to="/login"
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o Login
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <label htmlFor="email" className="block text-sm font-medium mb-1">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  type="email"
                  className="input pl-10"
                  placeholder="seu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoFocus
                />
              </div>
            </div>

            <button type="submit" className="btn-primary w-full mb-4" disabled={loading}>
              {loading ? 'Enviando...' : 'Enviar Email de Recuperação'}
            </button>

            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar para o Login
            </Link>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
